import type { Venue, VenueDiscoverySource } from '../types';

/**
 * Resolve "where did this venue come from?" attribution for a venue,
 * falling back to a Google Maps link for legacy venues whose `rowKey`
 * looks like a Google Place ID (created before `discoverySource` shipped).
 *
 * Returns `null` only when we have nothing useful to render.
 */
export interface ResolvedVenueSource {
  source: VenueDiscoverySource | 'google-places-inferred';
  /** Short label for the badge (e.g. "Google Maps", "Instagram"). */
  label: string;
  /** Bootstrap Icons class without the leading `bi `. */
  icon: string;
  /** Optional clickable URL — when omitted, render label-only. */
  url?: string;
  /** Optional tooltip / detail text. */
  detail?: string;
}

const PLACE_ID_RE = /^ChIJ[A-Za-z0-9_-]{20,}$/;

function isGooglePlaceIdRowKey(rowKey: string | undefined): boolean {
  return !!rowKey && PLACE_ID_RE.test(rowKey);
}

function googleMapsUrlFromPlaceId(placeId: string): string {
  return `https://www.google.com/maps/place/?q=place_id:${encodeURIComponent(placeId)}`;
}

/**
 * Map a stored discovery source to its display metadata.
 */
function describeSource(source: VenueDiscoverySource): { label: string; icon: string } {
  switch (source) {
    case 'google-places':
      return { label: 'Google Maps', icon: 'bi-google' };
    case 'instagram':
      return { label: 'Instagram', icon: 'bi-instagram' };
    case 'facebook':
      return { label: 'Facebook', icon: 'bi-facebook' };
    case 'website':
      return { label: 'Website', icon: 'bi-globe' };
    case 'event-extraction':
      return { label: 'Found in event post', icon: 'bi-search' };
    case 'submission':
      return { label: 'Submitted', icon: 'bi-send' };
    case 'manual':
      return { label: 'Added manually', icon: 'bi-pencil-square' };
  }
}

export function resolveVenueSource(venue: Pick<Venue, 'rowKey' | 'discoverySource' | 'discoverySourceUrl' | 'discoverySourceDetail'>): ResolvedVenueSource | null {
  // Explicit attribution wins.
  if (venue.discoverySource) {
    const meta = describeSource(venue.discoverySource);
    // For Google Places without an explicit URL, synthesize one from the
    // rowKey when it looks like a Place ID.
    let url = venue.discoverySourceUrl;
    if (!url && venue.discoverySource === 'google-places' && isGooglePlaceIdRowKey(venue.rowKey)) {
      url = googleMapsUrlFromPlaceId(venue.rowKey);
    }
    return {
      source: venue.discoverySource,
      label: meta.label,
      icon: meta.icon,
      url,
      detail: venue.discoverySourceDetail,
    };
  }

  // Heuristic fallback for legacy venues with a Google Place ID rowKey.
  if (isGooglePlaceIdRowKey(venue.rowKey)) {
    return {
      source: 'google-places-inferred',
      label: 'Google Maps',
      icon: 'bi-google',
      url: googleMapsUrlFromPlaceId(venue.rowKey),
      detail: 'Origin not recorded — link inferred from Place ID',
    };
  }

  return null;
}
